import { Zap, Upload, Network, LucideIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useChatStore } from '../stores/useChatStore.js';

export interface PluginSlot {
    id: string;
    label: string;
    icon: LucideIcon;
    onClick: () => void;
}

export interface SessionThread {
    id: string;
    name: string;
    status: 'active' | 'idle' | 'completed' | 'failed';
    onClick: () => void;
}

export interface PluginRegistry {
    pluginSlots: PluginSlot[];
    sessionThreads: SessionThread[];
}

const createPluginSlots = (): PluginSlot[] => {
    const manifestBasedSlots: PluginSlot[] = [];

    const legacySlots: PluginSlot[] = [
        {
            id: 'arcCore',
            label: 'ARC Core',
            icon: Zap,
            onClick: () => {
                console.log('ARC Core plugin activated');
            },
        },
        {
            id: 'arcDeploy',
            label: 'ARC Deploy',
            icon: Upload,
            onClick: () => {
                console.log('ARC Deploy plugin activated');
            },
        },
        {
            id: 'arcos',
            label: 'ARCOS',
            icon: Network,
            onClick: () => {
                console.log('ARCOS plugin activated');
            },
        },
    ];

    return [...manifestBasedSlots, ...legacySlots];
};

const useSessionThreads = (loadSession: (sessionId: string) => Promise<void>): SessionThread[] => {
    const [sessions, setSessions] = useState<{ id: string; title: string; status?: string }[]>([]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const API_BASE =
            (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ||
            'http://localhost:3000';
        let isMounted = true;
        fetch(`${API_BASE}/api/sessions`)
            .then(r => r.json())
            .then(json => {
                if (isMounted && json.success) {
                    setSessions(json.data);
                }
            })
            .catch(error => {
                console.error('Failed to fetch sessions:', error);
                if (isMounted) setSessions([]);
            });
        return () => {
            isMounted = false;
        };
    }, []);

    if (typeof window === 'undefined') {
        return [];
    }

    return sessions.map(s => ({
        id: s.id,
        name: s.title,
        status: (s.status as 'active' | 'idle' | 'completed' | 'failed') || 'idle',
        onClick: () => loadSession(s.id),
    }));
};

export const usePluginRegistry = (): PluginRegistry => {
    const { loadSession } = useChatStore();

    return {
        pluginSlots: createPluginSlots(),
        sessionThreads: useSessionThreads(loadSession),
    };
};

export const PluginRegistry = usePluginRegistry;
